// Settings → Day report (docs/plans/day-report.md): one email after each day with sales.
// The switch, the time and who gets it are synced settings that admins write; the list of
// people and "See a sample" need the internet. Your own "Send it to me" is the same
// opt-out as the link in the email (email_pref_get / email_pref_set).
import type { MemberRole } from '@ims/remote'
import { Avatar, Badge, Button, Card, Modal, Select, Switch, useToast } from '@ims/ui'
import { useEffect, useState } from 'react'
import { errorMessage, useOnline, useSettings } from '../hooks.ts'
import { createApi } from '../lib/api.ts'
import { updateOp } from '../lib/rows.ts'
import { useSession } from '../session.tsx'
import { roleAtLeast, useCan, useRepo, useWorkspace } from '../workspace.tsx'

interface Person {
  user_id: string
  role: MemberRole
  email: string | null
  name: string
}

/** Every half hour from noon to 11:30 PM, Nepal time. */
const TIMES = Array.from({ length: 24 }, (_, i) => {
  const minutes = 12 * 60 + i * 30
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  const value = `${String(h).padStart(2, '0')}:${m === 0 ? '00' : '30'}`
  return { value, label: timeLabel(value) }
})

/** "11:00 PM" from "23:00". */
export function timeLabel(value: string): string {
  const [h, m] = value.split(':').map(Number) as [number, number]
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

/** "Maya and Rabin", "Maya, Rabin and Sita". */
function names(list: string[]): string {
  const first = list.map((n) => n.split(/\s+/)[0]!)
  return first.length <= 1
    ? (first[0] ?? 'nobody')
    : `${first.slice(0, -1).join(', ')} and ${first.at(-1)}`
}

/** Who gets it under the rule the server uses (`day_report_recipients`). */
export function dayReportPeople(people: Person[], to: string[] | null): Person[] {
  return people.filter((p) =>
    to === null
      ? roleAtLeast(p.role, 'admin')
      : roleAtLeast(p.role, 'manager') && to.includes(p.user_id),
  )
}

export function DayReportSettings() {
  const settings = useSettings()
  const repo = useRepo()
  const toast = useToast()
  const { platform } = useSession()
  const { current } = useWorkspace()
  const online = useOnline()
  const canEdit = useCan('admin')
  const tenantId = current?.tenant_id ?? ''
  const [people, setPeople] = useState<Person[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [sample, setSample] = useState<{ subject: string; html: string } | null>(null)
  const [opening, setOpening] = useState(false)

  useEffect(() => {
    if (!tenantId || !online) return
    let active = true
    void (async () => {
      const { data: rows, error } = await platform.supabase
        .from('memberships')
        .select('user_id, role')
        .eq('tenant_id', tenantId)
      if (!active) return
      if (error) return toast.error(errorMessage(error))
      const managers = (rows ?? []).filter((r) => roleAtLeast(r.role as MemberRole, 'manager'))
      const { data: profiles } = await platform.supabase
        .from('profiles')
        .select('id, email, full_name')
        .in(
          'id',
          managers.map((r) => r.user_id as string),
        )
      if (!active) return
      const byId = new Map((profiles ?? []).map((p) => [p.id as string, p]))
      setPeople(
        managers.map((r) => {
          const p = byId.get(r.user_id as string)
          const email = (p?.email as string | undefined) ?? null
          return {
            user_id: r.user_id as string,
            role: r.role as MemberRole,
            email,
            name: (p?.full_name as string | undefined)?.trim() || email?.split('@')[0] || 'Someone',
          }
        }),
      )
    })()
    return () => {
      active = false
    }
  }, [platform, tenantId, online, toast])

  if (!settings) return null
  const to = Array.isArray(settings.day_report_to) ? (settings.day_report_to as string[]) : null
  const save = async (changes: Record<string, unknown>) => {
    setBusy(true)
    try {
      await repo.commit([updateOp('tenant_settings', settings, changes)])
      toast.success('Saved')
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }
  const getting = people ? dayReportPeople(people, to) : []
  const at = timeLabel(settings.day_report_at)
  const nowNepal = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kathmandu',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date())
  const when = nowNepal < settings.day_report_at ? 'today' : 'tomorrow'
  const tick = (person: Person, on: boolean) => {
    // From "owners and admins" to a list the first time someone is ticked or unticked.
    const base =
      to ?? (people ?? []).filter((p) => roleAtLeast(p.role, 'admin')).map((p) => p.user_id)
    const next = on
      ? [...new Set([...base, person.user_id])]
      : base.filter((id) => id !== person.user_id)
    void save({ day_report_to: next })
  }
  const openSample = async () => {
    setOpening(true)
    try {
      const res = await createApi(platform.apiUrl, platform.supabase).dayReportSample(tenantId)
      setSample(res)
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setOpening(false)
    }
  }

  return (
    <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-4">
      <Card className="p-0">
        <div className="p-4 sm:p-5">
          <Switch
            label="Email the day report"
            description="One email after each day with sales. Off until you turn it on."
            checked={settings.day_report}
            disabled={!canEdit || busy}
            onChange={(e) => void save({ day_report: e.target.checked })}
          />
        </div>
        {settings.day_report && (
          <div className="border-t border-border bg-primary-light/40 px-4 py-3 text-[13.5px] sm:px-5">
            <b className="block font-semibold text-ink">
              Next report: {when} at {at}
            </b>
            <span className="text-muted">
              {people
                ? `To ${names(getting.map((p) => p.name))}, covering ${when === 'today' ? 'today’s' : 'tomorrow’s'} bills. `
                : ''}
              Bills that reach the server later are noted in the next email.
            </span>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 border-t border-border px-4 py-3.5 sm:px-5">
          <span className="min-w-0 flex-1">
            <b className="block font-semibold text-ink">Send at</b>
            <span className="text-[12.5px] text-muted">Nepal time, on the half hour</span>
          </span>
          <div className="w-36 shrink-0">
            <Select
              aria-label="Send at"
              value={settings.day_report_at}
              disabled={!canEdit || busy}
              onChange={(e) => void save({ day_report_at: e.target.value })}
            >
              {TIMES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="border-t border-border px-4 pt-3.5 pb-2 sm:px-5">
          <b className="block font-semibold text-ink">Send to</b>
          <span className="text-[12.5px] text-muted">
            It shows money and sales by server, so only owners, admins and managers can get it.
          </span>
        </div>
        {!online ? (
          <p className="px-4 pb-4 text-[13px] text-muted sm:px-5">
            The list of people needs the internet.
          </p>
        ) : (
          (people ?? []).map((p) => {
            const on = getting.some((g) => g.user_id === p.user_id)
            return (
              <label
                key={p.user_id}
                className="flex items-center gap-3 border-t border-border px-4 py-2.5 sm:px-5"
              >
                <input
                  type="checkbox"
                  className="size-4 accent-primary"
                  checked={on}
                  disabled={!canEdit || busy}
                  onChange={(e) => tick(p, e.target.checked)}
                  aria-label={`Send to ${p.name}`}
                />
                <Avatar name={p.name} />
                <span className="min-w-0 flex-1">
                  <b className="block truncate font-semibold text-ink">{p.name}</b>
                  <span className="block truncate text-[12.5px] text-muted">{p.email}</span>
                </span>
                <Badge className="capitalize">{p.role}</Badge>
              </label>
            )
          })
        )}
        <div className="flex flex-wrap items-center gap-3 border-t border-border px-4 py-3.5 sm:px-5">
          <span className="min-w-0 flex-1 text-[12.5px] text-muted">
            {online ? 'Opens the report for today so far. Nothing is sent.' : 'Needs the internet.'}
          </span>
          <Button
            variant="secondary"
            disabled={!online}
            loading={opening}
            onClick={() => void openSample()}
          >
            See a sample
          </Button>
        </div>
      </Card>
      <SendItToMe />
      <Modal
        open={sample !== null}
        onClose={() => setSample(null)}
        title="The day report, today so far"
        subtitle={sample?.subject}
        size="wide"
        unsaved={false}
      >
        {sample && (
          <iframe
            title="The day report email"
            srcDoc={sample.html}
            sandbox=""
            className="h-[70vh] w-full rounded-card border border-border"
          />
        )}
      </Modal>
    </div>
  )
}

/** This person's own day report: the same choice as the link in the email. */
function SendItToMe() {
  const { platform } = useSession()
  const { current } = useWorkspace()
  const online = useOnline()
  const toast = useToast()
  const tenantId = current?.tenant_id ?? ''
  const [pref, setPref] = useState<{ email: string | null; on: boolean } | null>(null)

  useEffect(() => {
    if (!tenantId) return
    let active = true
    void platform.supabase
      .rpc('email_pref_get', { p_tenant_id: tenantId, p_kind: 'day_report' })
      .then(({ data, error }) => {
        if (!active) return
        if (error) toast.error(errorMessage(error))
        else setPref(data as { email: string | null; on: boolean })
      })
    return () => {
      active = false
    }
  }, [platform, tenantId, toast])

  const turn = async (on: boolean) => {
    if (!pref) return
    const before = pref
    setPref({ ...pref, on })
    const { data, error } = await platform.supabase.rpc('email_pref_set', {
      p_tenant_id: tenantId,
      p_kind: 'day_report',
      p_on: on,
    })
    if (error) {
      setPref(before)
      toast.error(errorMessage(error))
    } else setPref(data as { email: string | null; on: boolean })
  }

  return (
    <Card>
      <Switch
        className="[overflow-wrap:anywhere]"
        label="Send it to me"
        description={
          !online
            ? 'Needs the internet to change.'
            : pref === null
              ? 'Loading…'
              : `${pref.email ?? ''} · your own choice, the same as the link in the email`
        }
        checked={pref?.on ?? false}
        disabled={!online || pref === null || !pref.email}
        onChange={(e) => void turn(e.target.checked)}
      />
    </Card>
  )
}
